import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import type * as Highcharts from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { ChartThemeService } from 'src/app/ui-kit/services/chart-theme.service';
import { buildBrandChartOptions } from 'src/app/ui-kit/chart/base-chart-options';

/** Data point for `LineChartComponent`. `x` may be a number (timestamp) or a string category. */
export interface LineChartPoint {
  x: number | string;
  y: number;
}

/** A named series for the line chart. */
export interface LineChartSeries {
  name: string;
  data: LineChartPoint[];
  /** Optional explicit color. Defaults to the brand palette. */
  color?: string;
}

/**
 * Brand-themed Highcharts line chart. Automatically applies Blume fonts, tokens, and
 * dark-mode adaptation via `ChartThemeService`. Consumers supply data only; theming
 * is centralized.
 *
 * For tiny inline trends, use `SparklineComponent` instead.
 *
 * @example
 * <app-line-chart
 *   [series]="[{ name: 'Reading', data: points }]"
 *   [height]="320"
 *   ariaLabel="Reading measurements over time"
 * />
 *
 * @feature shared
 */
@Component({
  selector: 'app-line-chart',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HighchartsChartComponent],
  host: { class: 'block' },
  template: `
    <div [style.height.px]="height()" [attr.role]="'img'" [attr.aria-label]="ariaLabel()">
      <highcharts-chart [options]="options()" style="width: 100%; height: 100%; display: block;" />
    </div>
  `,
})
export class LineChartComponent {
  private readonly themeService = inject(ChartThemeService);

  /** Series to plot. */
  readonly series = input.required<LineChartSeries[]>();

  /** Chart height in px. @default 320 */
  readonly height = input<number>(320);

  /** Optional axis titles. */
  readonly xAxisTitle = input<string | undefined>(undefined);
  readonly yAxisTitle = input<string | undefined>(undefined);

  /** Accessible label. Required since charts convey information visually. */
  readonly ariaLabel = input.required<string>();

  /** Merged Highcharts options. */
  readonly options = computed<Highcharts.Options>(() => {
    const base = buildBrandChartOptions(this.themeService.theme()) as Highcharts.Options;
    const firstX = this.series()[0]?.data[0]?.x;
    const xAxisType: Highcharts.AxisTypeValue = typeof firstX === 'number' ? 'datetime' : 'category';

    return {
      ...base,
      chart: { ...base.chart, type: 'line', height: this.height() },
      xAxis: {
        ...(base.xAxis as Highcharts.XAxisOptions),
        type: xAxisType,
        title: { text: this.xAxisTitle() },
      },
      yAxis: {
        ...(base.yAxis as Highcharts.YAxisOptions),
        title: { text: this.yAxisTitle() },
      },
      series: this.series().map(s => ({
        type: 'line',
        name: s.name,
        color: s.color,
        data: s.data.map(p => (typeof p.x === 'number' ? [p.x, p.y] : { name: p.x, y: p.y })),
      })) as Highcharts.SeriesOptionsType[],
    };
  });
}
